/**
 * KnowledgeEntryModal - Create/edit form for a knowledge base entry
 */

import { Paperclip, X } from 'lucide-react';
import {
  AgentPhaseLabels,
  AgentPhases,
  KnowledgeCategories,
  KnowledgeCategoryLabels,
  type AgentPhase,
} from '@resolve-ai/shared';
import { Modal, Dropdown } from '../common';
import type { KnowledgeEntryFormData } from '../../hooks/useKnowledgeBasePage';
import { formatFileSize } from '../../lib/format-utils';

interface AttachmentFieldProps {
  file: File | null;
  isReplacing: boolean;
  onChange: (file: File | null) => void;
}

/** The "attach a file" row: a picker, or the selected file with a remove button. */
function AttachmentField({ file, isReplacing, onChange }: AttachmentFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {isReplacing ? 'Replace attached file (optional)' : 'Attach a file (optional)'}
      </label>
      {file ? (
        <div className="flex items-center justify-between gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
          <span className="flex items-center gap-2 min-w-0 text-sm text-gray-700 dark:text-gray-300">
            <Paperclip className="w-4 h-4 shrink-0" />
            <span className="truncate">{file.name}</span>
            <span className="text-gray-400 dark:text-gray-500 shrink-0">
              ({formatFileSize(file.size)})
            </span>
          </span>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="shrink-0 text-gray-400 hover:text-red-500"
            title="Remove file"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <input
          type="file"
          onChange={(e) => onChange(e.target.files?.[0] ?? null)}
          className="w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-indigo-50 dark:file:bg-indigo-900/30 file:text-indigo-700 dark:file:text-indigo-300 hover:file:bg-indigo-100"
        />
      )}
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {isReplacing
          ? 'Selecting a file replaces the current attachment - the agent reads whichever file is on the entry.'
          : 'Any file type is supported (PDF, spreadsheets, documents, etc.) - the agent reads it directly when this entry is relevant.'}
      </p>
    </div>
  );
}

interface PhaseSelectorProps {
  phases: AgentPhase[];
  onToggle: (phase: AgentPhase) => void;
}

const PHASE_OPTIONS = Object.values(AgentPhases);

/** Toggle buttons for which agent phases this entry applies to. */
function PhaseSelector({ phases, onToggle }: PhaseSelectorProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Applies to phases
      </label>
      <div className="flex flex-wrap gap-2">
        {PHASE_OPTIONS.map((phase) => {
          const isSelected = phases.includes(phase);
          return (
            <button
              key={phase}
              type="button"
              onClick={() => onToggle(phase)}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                isSelected
                  ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 text-indigo-700 dark:text-indigo-300'
                  : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
              }`}
            >
              {AgentPhaseLabels[phase]}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Leave all unselected to apply to every phase.
      </p>
    </div>
  );
}

interface PinnedCheckboxProps {
  pinned: boolean;
  disabled: boolean;
  onChange: (pinned: boolean) => void;
}

/** Pinned toggle, disabled with an explanatory tooltip for a file-backed entry. */
function PinnedCheckbox({ pinned, disabled, onChange }: PinnedCheckboxProps) {
  return (
    <label
      className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      title={disabled ? 'A file-backed entry cannot be pinned - it is always retrieved on demand' : undefined}
    >
      <input
        type="checkbox"
        checked={pinned}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
      />
      <span className="text-sm text-gray-700 dark:text-gray-300">
        Pin this entry (always included in the agent&apos;s prompt, rather than retrieved on demand)
      </span>
    </label>
  );
}

interface BodyFieldProps {
  isFileBacked: boolean;
  body: string;
  onChange: (body: string) => void;
}

/** The content/notes textarea - required free-text guidance for a text entry, optional notes for a file-backed one. */
function BodyField({ isFileBacked, body, onChange }: BodyFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {isFileBacked ? 'Notes for the agent' : 'Content'}
        {!isFileBacked && <span className="text-red-500"> *</span>}
      </label>
      <textarea
        value={body}
        onChange={(e) => onChange(e.target.value)}
        rows={isFileBacked ? 3 : 8}
        placeholder={
          isFileBacked
            ? 'Optional - tell the agent when and how to use the attached file...'
            : 'Write the actual guidance in markdown...'
        }
        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y whitespace-pre-wrap"
      />
    </div>
  );
}

interface ModalFooterProps {
  mode: 'create' | 'edit';
  isSaving: boolean;
  isFormValid: boolean;
  onClose: () => void;
  onSave: () => void;
}

/** Cancel / Create-or-Save-Changes footer buttons. */
function ModalFooter({ mode, isSaving, isFormValid, onClose, onSave }: ModalFooterProps) {
  const canSave = !isSaving && isFormValid;
  return (
    <>
      <button
        onClick={onClose}
        disabled={isSaving}
        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        onClick={onSave}
        disabled={!canSave}
        className={`px-4 py-2 rounded-xl transition-colors ${
          canSave
            ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
        }`}
      >
        {isSaving ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}
      </button>
    </>
  );
}

interface KnowledgeEntryModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  /** Whether the entry being edited already has an attachment. Ignored in create mode. */
  hasExistingAttachment: boolean;
  onClose: () => void;
  onSave: () => void;
  form: KnowledgeEntryFormData;
  onFormChange: (form: KnowledgeEntryFormData) => void;
  isFormValid: boolean;
  isSaving: boolean;
}

const CATEGORY_OPTIONS = Object.values(KnowledgeCategories).map((category) => ({
  label: KnowledgeCategoryLabels[category],
  value: category,
}));

export function KnowledgeEntryModal({
  isOpen,
  mode,
  hasExistingAttachment,
  onClose,
  onSave,
  form,
  onFormChange,
  isFormValid,
  isSaving,
}: KnowledgeEntryModalProps) {
  const togglePhase = (phase: AgentPhase) => {
    const isSelected = form.phases.includes(phase);
    onFormChange({
      ...form,
      phases: isSelected ? form.phases.filter((p) => p !== phase) : [...form.phases, phase],
    });
  };

  const isFileBacked = Boolean(form.file) || (mode === 'edit' && hasExistingAttachment);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Add Knowledge Entry' : 'Edit Knowledge Entry'}
      footer={
        <ModalFooter
          mode={mode}
          isSaving={isSaving}
          isFormValid={isFormValid}
          onClose={onClose}
          onSave={onSave}
        />
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => onFormChange({ ...form, title: e.target.value })}
            placeholder="e.g. API route conventions"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => onFormChange({ ...form, description: e.target.value })}
            maxLength={200}
            placeholder="Use this when... (this is what the agent reads to decide when to pull the entry)"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            A vague description means the agent never retrieves this entry. Be specific about when it applies.
          </p>
        </div>

        {(mode === 'create' || hasExistingAttachment) && (
          <AttachmentField
            file={form.file}
            isReplacing={mode === 'edit' && hasExistingAttachment}
            onChange={(file) => onFormChange({ ...form, file, pinned: file ? false : form.pinned })}
          />
        )}

        <BodyField
          isFileBacked={isFileBacked}
          body={form.body}
          onChange={(body) => onFormChange({ ...form, body })}
        />
        <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">
          The agent treats this as reference material, not instructions - it informs code and PR
          content but can&apos;t change the agent&apos;s own behavior, output language, tools, or
          safety rules.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Category
          </label>
          <Dropdown
            options={CATEGORY_OPTIONS}
            value={form.category}
            onChange={(value) => onFormChange({ ...form, category: value as typeof form.category })}
          />
        </div>

        <PhaseSelector phases={form.phases} onToggle={togglePhase} />

        <PinnedCheckbox
          pinned={form.pinned}
          disabled={isFileBacked}
          onChange={(pinned) => onFormChange({ ...form, pinned })}
        />
      </div>
    </Modal>
  );
}
